import { ImageErrorType } from "@next-platform/packages-ts-types";
import {
  ABORT_ERROR_MESSAGE,
  base64ToBlob,
  fileToDataURL,
  NotificationType,
  useAvatarImage,
  useTranslation,
} from "@next-platform/client-common-core";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import type { AvatarGeneratedImage } from "@/components/AvatarGeneratorModal/types";
import { AvatarGeneratedImageType } from "@/components/AvatarGeneratorModal/types";
import { useToast } from "@/hooks";

interface IUseAvatarGenerationProps {
  prompt: string;
  negativePrompt: string;
  guidanceImage: string | ArrayBuffer | null;
  fixedSeed: string;
  seedLocked: boolean;
  modelStyle: string;
  promptImageStyle: string;
  supportsPromptStyles: boolean;
  supportsNegativePrompt: boolean;
  supportsReferenceImage: boolean;
  generatedImages: AvatarGeneratedImage[];
  currentImageIndex: number;
  setAvatarUrl: (url: string) => void;
  setFixedSeed: (seed: string) => void;
  setGeneratedImages: Dispatch<SetStateAction<AvatarGeneratedImage[]>>;
  setCurrentImageIndex: (index: number) => void;
  setAbortController: (controller: AbortController | null) => void;
  setGuidanceImage: (image: string | ArrayBuffer | null) => void;
  setIsBlobLoading: (loading: boolean) => void;
  onSave: (key: string) => void;
  scrollToStart: () => void;
  scrollToImage: (index: number) => void;
}

// eslint-disable-next-line max-lines-per-function -- Orchestrator for generate/save/cancel/upload flows; splitting would fragment the shared state closure.
export const useAvatarGeneration = ({
  prompt,
  negativePrompt,
  guidanceImage,
  fixedSeed,
  seedLocked,
  modelStyle,
  promptImageStyle,
  supportsPromptStyles,
  supportsNegativePrompt,
  supportsReferenceImage,
  generatedImages,
  currentImageIndex,
  setAvatarUrl,
  setFixedSeed,
  setGeneratedImages,
  setCurrentImageIndex,
  setAbortController,
  setGuidanceImage,
  setIsBlobLoading,
  onSave,
  scrollToStart,
  scrollToImage,
}: IUseAvatarGenerationProps) => {
  const { t } = useTranslation();
  const { onUserGenerateAvatarImage: generateAvatarImage, onUserSaveAvatarImage: saveAvatarImage } = useAvatarImage();
  const { displayToast } = useToast();

  const uploadImageToSignedUrl = useCallback(async (signedUrl: string | undefined, body: Blob, contentType: string): Promise<void> => {
    // A save that carried the bytes is already stored and answers without an upload URL.
    if (!signedUrl) {
      return;
    }
    const response = await fetch(signedUrl, {
      method: "PUT",
      body,
      headers: { "Content-Type": contentType },
    });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
  }, []);

  const handleSave = useCallback(async () => {
    const imageData = generatedImages[currentImageIndex];
    const newImageData = imageData.base64;
    const bodyData = generatedImages.length > 1 ? newImageData : imageData.base64;
    // Both the prompt and the reference come from the image being saved, not from the modal's
    // current state: the user can generate, keep typing, then save an earlier image. The prompt
    // matters on its own because a client without a reference has it as its only record.
    //
    // The bytes go with the save, so the backend records the gallery row before it answers — and
    // because the reference rides along, it records it as generated rather than as an upload.
    const { data: result, error } = await saveAvatarImage({
      prompt: imageData.prompt,
      image_type: imageData.type,
      imageData: bodyData,
      ...(imageData.generationRef && { generation_ref: imageData.generationRef }),
    });

    if (result) {
      setIsBlobLoading(true);
      try {
        await uploadImageToSignedUrl(result.signed_url, base64ToBlob(bodyData, imageData.type), imageData.type);
        onSave(result?.key);
      } catch (uploadError) {
        const errorMessage =
          uploadError instanceof Error && uploadError.message.includes("status")
            ? t("common:modal.generateAvatar.saveImage.uploadErrorWithStatus", { errorMessage: uploadError.message })
            : t("common:modal.generateAvatar.saveImage.uploadError");

        displayToast({
          message: errorMessage,
          type: NotificationType.error,
        });
      } finally {
        setIsBlobLoading(false);
      }
    }

    if (error) {
      displayToast({
        message: error,
        type: NotificationType.error,
      });
    }
  }, [generatedImages, currentImageIndex, saveAvatarImage, setIsBlobLoading, onSave, displayToast, t, uploadImageToSignedUrl]);

  const onGenerate = useCallback(async () => {
    let seed = null;
    if (fixedSeed && seedLocked) {
      seed = fixedSeed;
    }

    const controller = new AbortController();
    setAbortController(controller);
    scrollToStart();

    const {
      data: result,
      error,
      errorTitle: avatarErrorTitle,
      errorPayload,
    } = await generateAvatarImage({
      prompt,
      negative_prompt: supportsNegativePrompt ? negativePrompt : "",
      image: supportsReferenceImage ? guidanceImage : null,
      seed,
      model_style: modelStyle,
      ...(supportsPromptStyles && { prompt_image_style: promptImageStyle }),
      abortSignal: controller.signal,
    });

    if (result?.base64) {
      setAvatarUrl(`data:${result.type};base64,${result.base64}`);
      setFixedSeed(result?.seed?.toString());
      const newImage: AvatarGeneratedImage = {
        ...result,
        id: uuidv4(),
        generationType: AvatarGeneratedImageType.GENERATION,
        prompt,
        negativePrompt,
        generationRef: result.generation_ref,
      };

      setGeneratedImages((prev: AvatarGeneratedImage[]) => [newImage, ...prev]);
      setCurrentImageIndex(0);

      scrollToImage(0);
    } else if (errorPayload?.errorCode.includes(ImageErrorType.NSFW_CONTENT_NOT_ALLOWED_FOR_UNVERIFIED_USERS.toString())) {
      setGeneratedImages((prev: AvatarGeneratedImage[]) => [
        {
          ...(result ?? {}),
          seed: 0,
          prompt,
          negativePrompt,
          isNsfw: true,
        } as AvatarGeneratedImage,
        ...prev,
      ]);
      setCurrentImageIndex(0);
      scrollToImage(0);
    } else if (error && error !== ABORT_ERROR_MESSAGE) {
      displayToast({
        message: error,
        title: avatarErrorTitle ?? undefined,
        type: NotificationType.error,
      });
    }

    setAbortController(null);
  }, [
    fixedSeed,
    seedLocked,
    supportsPromptStyles,
    supportsNegativePrompt,
    supportsReferenceImage,
    promptImageStyle,
    modelStyle,
    prompt,
    negativePrompt,
    guidanceImage,
    setAbortController,
    scrollToStart,
    generateAvatarImage,
    setAvatarUrl,
    setFixedSeed,
    setGeneratedImages,
    setCurrentImageIndex,
    scrollToImage,
    displayToast,
  ]);

  const cancelGeneration = useCallback(
    (abortController: AbortController | null) => {
      if (abortController) {
        abortController.abort();
      }
      setAbortController(null);
    },
    [setAbortController],
  );

  const handleImageUpload = useCallback(
    (file: File) => {
      // Swallow decode errors silently — the user can retry the upload; no logger is wired into this hook.
      fileToDataURL(file)
        .then(setGuidanceImage)
        .catch(() => undefined);
    },
    [setGuidanceImage],
  );

  return {
    handleSave,
    handleGenerate: onGenerate,
    cancelGeneration,
    handleImageUpload,
  };
};
